import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usersApi } from '@/lib/users/users.api';
import { SympheniaLogo } from '@/components/SympheniaLogo';
import { clearAccessToken } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  // needed to clear any existing admin session from memory (without server logout)
  // so LoginPage's isAuthenticated guard doesn't redirect away after invite acceptance
  const clearSession = () => {
    clearAccessToken();
    useAuthStore.setState({ user: null, isAuthenticated: false });
  };

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (!token) {
      setError(t('users.inviteExpired'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await usersApi.acceptInvite(token, password);
      setSuccess(true);
      // Clear any existing session from memory so LoginPage doesn't auto-redirect
      // an already-authenticated admin (e.g. who forwarded the invite to themselves)
      clearSession();
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('users.inviteExpired');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm">
        <div className="mb-6 text-center">
          <div className="mb-3 flex justify-center">
            <SympheniaLogo size={24} context="content" />
          </div>
          <h2 className="mt-2 text-lg font-semibold text-foreground">{t('users.inviteAccepted')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('auth.chooseNewPassword')}</p>
        </div>

        {success ? (
          <div className="rounded-md bg-green-50 p-4 text-center text-sm text-green-700">
            {t('users.inviteAccepted')} — {t('auth.redirectingToLogin')}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('auth.newPassword')}
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('auth.passwordConfirm')}
              </label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t('common.loading') : t('users.inviteAccepted')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
